import { defineStore } from 'pinia';

import {
  GrantableRoleResponse,
  LoginUserRepository,
} from '@/repositories/generated';
import {
  ERole,
  LoginUserResponse,
  TRole,
} from '@/types';

type LoginUserState = Partial<{
  loginUser: LoginUserResponse;
  grantableRole: GrantableRoleResponse[];
}>;

export const useLoginUserStore = defineStore('loginUser', {
  state: (): LoginUserState => ({
    loginUser: undefined,
    grantableRole: undefined,
  }),
  actions: {
    async getLoginUser(): Promise<LoginUserResponse> {
      if (this.loginUser) {
        return this.loginUser;
      }

      this.loginUser = (await LoginUserRepository.detail()) as LoginUserResponse;

      return this.loginUser;
    },
    async getLoginUserGrantableRole(): Promise<GrantableRoleResponse[]> {
      if (this.grantableRole) {
        return this.grantableRole;
      }

      this.grantableRole = await LoginUserRepository.grantableRole();

      return this.grantableRole;
    },
  },
  getters: {
    // eslint-disable-next-line complexity
    autocompleteModel: (state) => {
      const { loginUser } = state;

      let isAcquirerAutocompleteDisabled = false;
      let isCompanyAutocompleteDisabled = false;
      let isShopAutocompleteDisabled = false;

      let isIssuerAutocompleteDisabled = false;
      let isWalletServiceAutocompleteDisabled = false;

      switch (loginUser?.role.code as TRole) {
        case ERole.ACQUIRER_MANAGER:
        case ERole.ACQUIRER_STAFF:
          isAcquirerAutocompleteDisabled = true;
          break;
        case ERole.COMPANY_MANAGER:
        case ERole.COMPANY_STAFF:
          isAcquirerAutocompleteDisabled = true;
          isCompanyAutocompleteDisabled = true;
          break;
        case ERole.SHOP_MANAGER:
        case ERole.SHOP_STAFF:
          isAcquirerAutocompleteDisabled = true;
          isCompanyAutocompleteDisabled = true;
          isShopAutocompleteDisabled = true;
          break;
        case ERole.VALUE_ISSUER_MANAGER:
        case ERole.VALUE_ISSUER_STAFF:
          isIssuerAutocompleteDisabled = true;
          break;
        case ERole.WALLET_SERVICE_MANAGER:
        case ERole.WALLET_SERVICE_STAFF:
          isIssuerAutocompleteDisabled = true;
          isWalletServiceAutocompleteDisabled = true;
          break;
        default:
          break;
      }

      const loginAcquirer = isAcquirerAutocompleteDisabled ? loginUser?.acquirer : undefined;
      const loginCompany = isCompanyAutocompleteDisabled ? loginUser?.company : undefined;
      const loginShop = isShopAutocompleteDisabled ? loginUser?.shop : undefined;

      const loginIssuer = isIssuerAutocompleteDisabled ? loginUser?.issuer : undefined;
      const loginWalletService = isWalletServiceAutocompleteDisabled ? loginUser?.walletService : undefined;

      return {
        isAcquirerAutocompleteDisabled,
        isCompanyAutocompleteDisabled,
        isShopAutocompleteDisabled,
        isIssuerAutocompleteDisabled,
        isWalletServiceAutocompleteDisabled,
        loginAcquirer,
        loginCompany,
        loginShop,
        loginIssuer,
        loginWalletService,
      };
    },
  },
});
